import { Package, User } from "lucide-react";
import { Card, StatusDot } from "../../components/ui/patrones";
import { Boton, Etiqueta } from "../../components/ui/primitivos";
import type { OrdenListada } from "../../lib/consola-api";
import { etiquetaDeEstado, PUNTO, TONO } from "./estados";

/**
 * Las vistas de tarjetas y de lista del tablero (SPEC 0003, fase 3).
 *
 * `imprenta` tiene **cuatro** vistas del mismo trabajo —tabla, kanban, tarjetas
 * y lista— y guarda cuál se eligió. No son adornos: la tabla sirve para buscar
 * una OT puntual, las tarjetas para barrer el estado general, y la lista para
 * leer muchas seguidas sin el ruido de las columnas.
 *
 * La tabla usa `DataTable` y el kanban tiene su propio archivo; acá viven las
 * dos que son puramente presentación.
 */

/** El vacío es el mismo en las tres vistas no-tabla: borde punteado y una razón. */
export function SinResultados({ mensaje }: { mensaje: string }) {
  return (
    <div className="rounded-lg border border-neutral-300 border-dashed bg-neutral-50 py-12 text-center">
      <Package className="mx-auto h-12 w-12 text-neutral-400" />
      <h3 className="mt-2 font-semibold text-neutral-900 text-sm">No hay órdenes</h3>
      <p className="mt-1 text-neutral-500 text-sm">{mensaje}</p>
    </div>
  );
}

export function VistaTarjetas({
  ordenes,
  alAbrir,
}: {
  ordenes: readonly OrdenListada[];
  alAbrir: (o: OrdenListada) => void;
}) {
  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
      {ordenes.map((o) => (
        <Card key={o.id} className="flex h-full flex-col transition-shadow hover:shadow-md">
          <div className="flex items-start justify-between p-6 pb-3">
            <div>
              <h3 className="mb-2 font-semibold text-lg text-neutral-900 leading-none tracking-tight">
                {o.numero}
              </h3>
              <div className="flex items-center gap-1.5 text-neutral-500 text-sm">
                <StatusDot estado={PUNTO[o.estado] ?? "borrador"} />
                {etiquetaDeEstado(o.estado)}
              </div>
            </div>
            <Etiqueta tono={TONO[o.estado] ?? "neutral"}>{etiquetaDeEstado(o.estado)}</Etiqueta>
          </div>

          <div className="flex-1 space-y-2 p-6 pt-0 text-sm">
            <div className="flex items-center text-neutral-500">
              <User className="mr-2 h-4 w-4 shrink-0" />
              <span className="truncate">{o.cliente}</span>
            </div>
            <div className="flex items-center text-neutral-500">
              <Package className="mr-2 h-4 w-4 shrink-0" />
              <span className="truncate">{o.producto}</span>
            </div>
            <div className="flex items-center justify-between border-neutral-200 border-t pt-2">
              <span className="text-neutral-500">Cantidad:</span>
              <span className="font-medium">{o.cantidad}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-neutral-500">Responsable:</span>
              <span className="font-medium">{o.responsable ?? "sin asignar"}</span>
            </div>
          </div>

          <div className="border-neutral-200 border-t p-3 text-right">
            <Boton variante="fantasma" onClick={() => alAbrir(o)}>
              Abrir
            </Boton>
          </div>
        </Card>
      ))}
    </div>
  );
}

/**
 * Lista: una fila alta por OT, con todo en una línea.
 *
 * Es la vista para recorrer muchas seguidas — cada fila entra de un vistazo sin
 * saltar entre columnas.
 */
export function VistaLista({
  ordenes,
  alAbrir,
}: {
  ordenes: readonly OrdenListada[];
  alAbrir: (o: OrdenListada) => void;
}) {
  return (
    <div className="space-y-3">
      {ordenes.map((o) => (
        <Card key={o.id} className="flex items-center gap-4 p-4 transition-shadow hover:shadow-md">
          <StatusDot estado={PUNTO[o.estado] ?? "borrador"} />
          <span className="w-36 shrink-0 font-mono text-sm">{o.numero}</span>
          <span className="w-48 shrink-0 truncate font-medium">{o.cliente}</span>
          <span className="flex-1 truncate text-neutral-500">
            {o.producto} <span className="text-neutral-400">×{o.cantidad}</span>
          </span>
          <span className="w-40 shrink-0 truncate text-neutral-500 text-sm">
            {o.responsable ?? "sin asignar"}
          </span>
          <Etiqueta tono={TONO[o.estado] ?? "neutral"}>{etiquetaDeEstado(o.estado)}</Etiqueta>
          <Boton variante="fantasma" onClick={() => alAbrir(o)}>
            Abrir
          </Boton>
        </Card>
      ))}
    </div>
  );
}
